import React, { useState, useEffect, FC } from "react";
import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Badge,
  Modal,
  ModalBody,
  Row,
  Col,
} from "reactstrap";
import { Icon, Button, TooltipComponent } from "../../../components/Component";
import Swal from "sweetalert2";
import workCategoryApi from "../../../api/master/workCategory";
import { useSelector } from "react-redux";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import moment from "moment";
import AddModal from "./AddModalWorkCategory";
import EditModal from "./EditModalWorkCategory";
import Export from "../../../components/export/Export";
import { Flex, Spin, Empty } from "antd";

interface WorkCategoryItem {
  id: string;
  referenceID: string;
  workCategoryID: string;
  workCategoryName: string;
  categoryName: string;
  description?: string;
  status: string;
  createdDate: string;
}

const WorkCategory: FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { data: accessibility } = useSelector((state: any) => state.accessibility);
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });
  const [onSearchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);

  const [modal, setModal] = useState({
    edit: false,
    add: false,
  });
  const [viewModal, setViewModal] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [],
  });
  const [editId, setEditedId] = useState<string>("");
  const [detail, setDetail] = useState<WorkCategoryItem | null>(null);
  const [formData, setFormData] = useState({
    referenceID: "",
    categoryName: "",
    description: "",
    workCategoryID: "",
  });
  const [editFormData, setEditFormData] = useState({
    referenceID: "",
    categoryName: "",
    description: "",
    workCategoryID: "",
    status: "Active",
  });
  const [rowData, setRowData] = useState<WorkCategoryItem[]>([]);
  const [originalData, setOriginalData] = useState<WorkCategoryItem[]>([]);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = rowData.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const loadDetail = (id: string) => {
    const item = rowData.find((item) => item.id === id);
    setDetail(item || null);
  };

  const closeModal = () => {
    setModal({ ...modal, add: false });
    resetForm();
    setAlertInfo({
      type: "",
      message: "",
      errors: [],
    });
  };

  const closeEditModal = () => {
    setModal({ ...modal, edit: false });
    setAlertInfo({
      type: "",
      message: "",
      errors: [],
    });
  };

  const resetForm = () => {
    setFormData({
      referenceID: "",
      categoryName: "",
      description: "",
      workCategoryID: "",
    });
  };

  const onFormSubmit = async (submitData: any) => {
    const response = await workCategoryApi.createWorkCategory(submitData);
    const { status, message } = response;
    if (status) {
      Swal.fire("Created!", "Work Category has been created", "success");
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
      setModal({ ...modal, add: false });
      fetchData();
    } else {
      let errorMessage = "An error occurred";
      const fullErrorMessage = `${errorMessage}\n${message.join(", ")}`;
      setAlertInfo({
        type: "error",
        message: fullErrorMessage,
        errors: [],
      });
    }
  };

  const onEditSubmit = async (submitData: any) => {
    const response = await workCategoryApi.updateWorkCategoryById(editId, submitData);
    const { status, message } = response;
    if (status) {
      Swal.fire("Updated!", "Work Category has been updated", "success");
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
      setModal({ ...modal, edit: false });
      fetchData();
    } else {
      let errorMessage = "An error occurred";
      const fullErrorMessage = `${errorMessage}\n${message.join(", ")}`;
      setAlertInfo({
        type: "error",
        message: fullErrorMessage,
        errors: [],
      });
    }
  };

  const onEditClick = (id: string) => {
    const item = rowData.find((item) => item.id === id);
    if (item) {
      setEditFormData({
        referenceID: item.referenceID,
        workCategoryID: item.workCategoryID,
        categoryName: item.categoryName,
        description: item.description || "",
        status: item.status || "Active",
      });
      setEditedId(id);
      setModal({ ...modal, edit: true });
    }
  };

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } = accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data, status, message } = await workCategoryApi.fetchWorkCategories();
      if (status) {
        setRowData(data);
        setOriginalData(data);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (onSearchText !== "") {
      const searchText = onSearchText.toLowerCase();
      const filteredData = originalData.filter((item) => {
        return Object.values(item).some((value) => {
          return value && value.toString().toLowerCase().includes(searchText);
        });
      });
      setRowData(filteredData);
    } else {
      setRowData(originalData);
    }
  }, [onSearchText, originalData]);

  const columns = [
    {
      title: "Reference ID",
      dataIndex: "referenceID",
      key: "referenceID",
      width: 200,
      fixed: "left"
    },
    {
      title: "Work Category ID",
      dataIndex: "workCategoryID",
      key: "workCategoryID",
      width: 200,
    },
    {
      title: "Work Type",
      dataIndex: "workCategoryName",
      key: "workCategoryName",
      width: 250,
    },
    {
      title: "Category Name",
      dataIndex: "categoryName",
      key: "categoryName",
      width: 250,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      render: (text: string) => (
        <span>
          {text && text.length > 25 ? `${text.substring(0, 25)}...` : text || '-'}
          {text && text.length > 25 && (
            <TooltipComponent
              iconClass="info-circle text-info"
              icon="info"
              id={`desc-tooltip-${text}`}
              direction="top"
              text={text}
              containerClassName="d-inline-block ms-1"
            />
          )}
        </span>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 200,
      render: (text: string) => <span>{moment(text).format("DD-MMM-YYYY")}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (text: string) => (
        <span className={`tb-status text-${text === "Active" ? "success" : "danger"}`}>
          {text}
        </span>
      ),
    },
    {
      title: "Details",
      dataIndex: "details",
      key: "details",
      width: 100,
      render: (text: any, record: any) => (
        <ul className="nk-tb-actions">
          {allowedActions?.edit && (
            <li onClick={() => onEditClick(record.id)}>
              <TooltipComponent
                tag="a"
                containerClassName="btn btn-trigger btn-icon"
                id={"edit" + record.id}
                icon="edit-alt-fill"
                direction="top"
                text="Edit"
                iconClass={undefined}
              />
            </li>
          )}
          <li>
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                <Icon name="more-h"></Icon>
              </DropdownToggle>
              <DropdownMenu end>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <Link to="#" onClick={() => {
                      loadDetail(record.id);
                      setViewModal(true);
                    }}>
                      <Icon name="focus"></Icon>
                      <span>View Details</span>
                    </Link>
                  </li>
                  {allowedActions?.edit && (
                    <li onClick={() => onEditClick(record.id)}>
                      <DropdownItem tag="a" href="#edit" onClick={(ev) => ev.preventDefault()}>
                        <Icon name="edit" />
                        <span>Edit</span>
                      </DropdownItem>
                    </li>
                  )}
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      ),
    },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="work-category-list"
        title="Work Category"
        columns={columns}
        extra={{
          excelExport: true,
        }}
        refresh={onSearchText}
        topRightJSX={
          <>
            {allowedActions?.add && (
              <Button
                color="primary"
                onClick={() => {
                  setModal({ ...modal, add: true });
                  setAlertInfo({
                    type: "",
                    message: "",
                    errors: [],
                  });
                }}
              >
                <Icon name="plus"></Icon>
                <span>Add</span>
              </Button>
            )}
          </>
        }
        searchProps={{
          value: onSearchText,
          onChange: onFilterChange,
          placeholder: "Search...",
        }}
        paginationProps={{
          itemPerPage,
          totalItems: rowData.length,
          currentPage,
          paginate,
          setItemPerPage,
        }}
        loading={isLoading}
        emptyData={rowData.length === 0 && !isLoading}
      />

      <AddModal
        modal={modal.add}
        formData={formData}
        setFormData={setFormData}
        closeModal={closeModal}
        onSubmit={onFormSubmit}
        filterStatus
        alertInfo={alertInfo}
      />
      <EditModal
        modal={modal.edit}
        formData={editFormData}
        setFormData={setEditFormData}
        closeModal={closeEditModal}
        onSubmit={onEditSubmit}
        filterStatus
        alertInfo={alertInfo}
      />

      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        className="modal-dialog-centered"
        size="lg"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setViewModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="nk-modal-head">
            <h4 className="nk-modal-title title">
              Work Category Details -{" "}
              <small className="text-primary"> {detail?.workCategoryID}</small>
            </h4>
          </div>
          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              <Col lg={6}>
                <span className="sub-text">Internal ID</span>
                <span className="caption-text">{detail?.referenceID}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Work Category</span>
                <span className="caption-text text-break">
                  {detail?.workCategoryID}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Category Name</span>
                <span className="caption-text">{detail?.categoryName}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Status</span>
                <Badge
                  color={detail?.status === "Active" ? "success" : "danger"}
                  size="md"
                >
                  {detail?.status}
                </Badge>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Description</span>
                <span className="caption-text"> {detail?.description}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {" "}
                  {detail?.createdDate && moment(detail.createdDate).format("DD MMM YYYY")}
                </span>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default WorkCategory;